import React from 'react';
import type { InputActionMeta } from 'react-select';
import { useDebouncedCallback } from '@tanstack/react-pacer';

import { TrackableItem, TrackableList } from 'types/trackables';

import { useTrackableItemsCrossLists } from 'trackables/hooks/useTrackableItems';

import { Select, SelectProps } from 'components/Select/Select';

type TrackableItemSelectProps<IsMulti extends boolean = false> = SelectProps<
  TrackableItem,
  IsMulti
> & {
  trackableLists?: TrackableList[];
  limitPerList?: number;
};

const DEFAULT_LIMIT_PER_LIST = 50;

export function TrackableItemSelect<IsMulti extends boolean = false>({
  trackableLists,
  limitPerList = DEFAULT_LIMIT_PER_LIST,
  placeholder = 'Watchlist items',
  isLoading,
  onInputChange,
  getOptionLabel,
  getOptionValue,
  ...selectProps
}: TrackableItemSelectProps<IsMulti>) {
  const [searchTerm, setSearchTerm] = React.useState('');

  const normalizedListIds = React.useMemo(() => {
    if (!trackableLists || !trackableLists.length) {
      return [];
    }

    const ids: string[] = [];
    for (const list of trackableLists) {
      if (list?.id) {
        ids.push(list.id);
      }
    }

    const unique = Array.from(new Set(ids));
    unique.sort();
    return unique;
  }, [trackableLists]);

  const listNameLookup = React.useMemo(() => {
    if (!trackableLists) {
      return new Map<string, string>();
    }

    return new Map(trackableLists.map((list) => [list.id, list.name]));
  }, [trackableLists]);

  const {
    data: itemsResponse,
    isInitialLoading,
    isFetching,
  } = useTrackableItemsCrossLists({
    queryKey: [
      'trackable-item-select',
      normalizedListIds,
      searchTerm,
      limitPerList,
    ],
    params: {
      limit: limitPerList,
      trackable_list_ids: normalizedListIds,
      trackable_item_names: searchTerm ? [searchTerm] : undefined,
    },
    enabled: normalizedListIds.length > 0,
    keepPreviousData: true,
  });

  const itemsData = itemsResponse?.data;

  const sortedItems = React.useMemo(() => {
    if (!itemsData) {
      return [];
    }

    const items = [...itemsData];
    items.sort((a, b) => {
      const listNameA = listNameLookup.get(a.trackable_list_id) ?? '';
      const listNameB = listNameLookup.get(b.trackable_list_id) ?? '';

      if (listNameA.toLocaleLowerCase() !== listNameB.toLocaleLowerCase()) {
        return listNameA.localeCompare(listNameB);
      }

      const nameA = a.name ?? '';
      const nameB = b.name ?? '';
      if (nameA.toLocaleLowerCase() !== nameB.toLocaleLowerCase()) {
        return nameA.localeCompare(nameB);
      }

      return a.id.localeCompare(b.id);
    });

    return items;
  }, [itemsData, listNameLookup]);

  const handleSearchTermChange = useDebouncedCallback(
    (value: string) => {
      setSearchTerm(value);
    },
    { wait: 150 }
  );

  const combinedInputChange = React.useCallback(
    (value: string, meta: InputActionMeta) => {
      handleSearchTermChange(value);

      if (onInputChange) {
        onInputChange(value, meta);
      }

      return value;
    },
    [handleSearchTermChange, onInputChange]
  );

  const defaultGetOptionLabel = React.useCallback(
    (item: TrackableItem) => {
      const listName = listNameLookup.get(item.trackable_list_id);
      return listName ? `${listName} → ${item.name}` : (item.name ?? item.id);
    },
    [listNameLookup]
  );

  const defaultGetOptionValue = React.useCallback(
    (item: TrackableItem) => item.id,
    []
  );

  const combinedIsDisabled =
    normalizedListIds.length === 0 || selectProps.isDisabled;

  const combinedIsLoading =
    isLoading || isInitialLoading || (isFetching && !itemsData);

  return (
    <Select
      {...selectProps}
      placeholder={placeholder}
      options={sortedItems}
      isDisabled={combinedIsDisabled}
      isLoading={combinedIsLoading}
      onInputChange={combinedInputChange}
      getOptionLabel={getOptionLabel ?? defaultGetOptionLabel}
      getOptionValue={getOptionValue ?? defaultGetOptionValue}
    />
  );
}
